import { useEarlyConsolidation } from '../../../consolidation/Consolidation';
import { OutOfCoreTaskBase } from "../OutOfCoreTaskBase";
import { OutOfCoreTileManager } from "../../OutOfCoreTileManager";
import { CallbackTask } from "../CallbackTask";
import { MeshFlags } from "./../../../MeshFlags";
import { USE_GEOMETRY_OOC } from "../../../../globals";

/** @import {BvhNode} from "../../BvhNode.js" */
export class LoadFraglistTask extends OutOfCoreTaskBase {

  /**
   *
   * @param {OutOfCoreTileManager} outOfCoreTileManager The OutOfCoreTileManager instance
   * @param {BvhNode} node The BVH node
   */
  constructor(outOfCoreTileManager, node) {
    super(outOfCoreTileManager);

    this.node = node;
  }

  async *execute(deadline) {
    const loader = this.node.model.loader;
    const node = this.node;
    const model = node.outOfCoreTileManager.model;

    if (useEarlyConsolidation(node.model) && loader.loadFromCache) {
      const data = await loader.getCachedFragListChunk(this.node.nodeId, this.node);

      // Need to do this before parsing, otherwise mat hashes might be missing in tryToActivateFragment
      await loader.svf.materialHashlistPromise;

      if (!data) {
        // TODO: Once server side BVH chunks are available, request the chunk from the server
        throw new Error(`Cache corrupted. Failed to load fragment list chunk for node ${this.node.nodeId} from cache.`);
      }

      deadline = yield;
      yield* loader.processFragListChunk(this.node.nodeId, data, deadline);
    }

    // Update the node's bounding box after the fragment list has been loaded
    const bvhModelIterator = model.getIterator();
    bvhModelIterator.updateBounds(this.node.nodeId);

    if (USE_GEOMETRY_OOC) {
      const renderBatch = bvhModelIterator.getGeomScenes()[node.nodeId];
      if (renderBatch) {
        // After we have loaded the fragment list, we mark all fragments as not yet loaded
        // (by setting the flag MESH_SWAPPED_OUT_OF_CORE to true). Once the geometries have
        // been loaded, we will set the flag to false.
        const renderBatchIndices = renderBatch.getIndices();
        for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
          const fragId = renderBatchIndices[i];
          if (model.getFragmentList().setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, true)) {
            model.getFragmentList().setFragOff(fragId, true);
          }
        }
      }
    }
  }
}

/**
 * Creates a task to load the fragment list
 *
 * @param {BvhNode} node - The ID of the BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeFraglistLoadedTasks(node, stage) {
  if (useEarlyConsolidation(node.model) || USE_GEOMETRY_OOC) {
    const loader = node.model.loader;
    if (loader.loadFromCache && !loader.hasChunkBeenProcessed?.(node.nodeId) || USE_GEOMETRY_OOC) {
      node.addTask(new LoadFraglistTask(node.outOfCoreTileManager, node), stage);
    }
    // nothing to do otherwise
  } else {
    // The fragment list has already been loaded, so we can trigger the bounding box
    // update immediately
    node.model.getIterator().updateBounds(node.nodeId);
  }
}